import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    model,
    output,
    TemplateRef,
    ViewEncapsulation,
} from "@angular/core";
import { NgTemplateOutlet } from "@angular/common";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { AngBridge } from "../../util/ang.bridge.util";
import { S25CollapseChevronComponent } from "../s25-collapse-chevron/s25.collapse.chevron.component";

export type Header = string | TemplateRef<void> | { text: string; level: 1 | 2 | 3 | 4 | 5 | 6 };

@TypeManagerDecorator("s25-simple-collapse")
@Component({
    selector: "s25-simple-collapse",
    template: `
        <div class="c-sectionHead" (click)="expanded.set(!expanded())">
            <div class="header-container">
                @if (headerTemplate(); as headerTemplate) {
                    <ng-container [ngTemplateOutlet]="headerTemplate" />
                } @else {
                    @switch (headerLevel()) {
                        @case (1) {
                            <h1>{{ headerText() }}</h1>
                        }
                        @case (2) {
                            <h2>{{ headerText() }}</h2>
                        }
                        @default {
                            <h3>{{ headerText() }}</h3>
                        }
                        @case (4) {
                            <h4>{{ headerText() }}</h4>
                        }
                        @case (5) {
                            <h5>{{ headerText() }}</h5>
                        }
                        @case (6) {
                            <h6>{{ headerText() }}</h6>
                        }
                    }
                }
            </div>
            <div class="buttons">
                <s25-ng-button
                    class="button"
                    [type]="'link'"
                    [ariaLabel]="titleText()"
                    (clicked)="expanded.set(!expanded())"
                    (click)="$event.stopPropagation()"
                >
                    <s25-ng-collapse-chevron [collapsedOrientation]="'down'" [expanded]="expanded()" />
                </s25-ng-button>
                @if (hasClose()) {
                    <s25-ng-icon-button [type]="'close'" [ariaLabel]="'Close'" (clicked)="closed.emit()" />
                }
            </div>
        </div>
        <div class="content-wrapper" [hidden]="!expanded()">
            <ng-content></ng-content>
        </div>
    `,
    styles: `
        @property --simple-collapse--bg-color {
            syntax: "<color>";
            inherits: true;
            initial-value: initial;
        }
        @property --simple-collapse--header-bg-color {
            syntax: "<color>";
            inherits: true;
            initial-value: initial;
        }
        @property --simple-collapse--border-color {
            syntax: "<color>";
            inherits: true;
            initial-value: initial;
        }
        @property --simple-collapse--highlight-color {
            syntax: "<color>";
            inherits: true;
            initial-value: transparent;
        }
        @property --simple-collapse--padding {
            syntax: "<length>+";
            inherits: true;
            initial-value: 0px;
        }

        :host {
            display: block;
            margin-bottom: 10px;
        }

        :host.simple-collapse--wrapper {
            --border-color: var(--simple-collapse--border-color, var(--color--border));
            position: relative;
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius);
            box-shadow: 0 0 3px rgba(0, 0, 0, 0.07);
            background-color: var(--simple-collapse--bg-color, var(--color--surface-bg, #fff));
            color: var(--color--text, rgba(0, 0, 0, 0.8));
            font-size: 1rem;
            font-weight: 400;

            &::before {
                content: "";
                position: absolute;
                /* TODO: consider removing this */
                z-index: 21;
                inset-block: -1px;
                inset-inline-start: -1px;
                border-inline-start: var(--border-radius) solid var(--simple-collapse--highlight-color, transparent);
                border-top-left-radius: var(--border-radius);
                border-bottom-left-radius: var(--border-radius);
            }
        }

        .c-sectionHead {
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            position: relative;
            z-index: 20;
            border-radius: var(--border-radius--inner);
            background-color: var(
                --simple-collapse--header-bg-color,
                var(--simple-collapse--bg-color, var(--color--surface-2-bg, #f3f3f3))
            );

            .expanded & {
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
            }

            .header-container {
                align-content: center;
            }

            h1,
            h2,
            h3,
            h4,
            h5,
            h6 {
                padding-inline-start: 0.5rem;
                margin: 0;
                padding-block: 6px;
                font-weight: 600;
                font-size: 1rem !important;
            }
        }

        .content-wrapper {
            border-top: 1px solid var(--border-color);
            padding: var(--simple-collapse--padding, 0);
        }

        .buttons {
            display: flex;
            float: right;
            padding: 4px;
        }

        .button {
            font-size: 1.25em;
        }

        :host[data-buttons-side="left"] .header-container {
            order: 1;
            flex-grow: 1;
        }
    `,
    host: {
        class: "simple-collapse--wrapper",
        "[class.expanded]": "expanded()",
        "[attr.data-buttons-side]": "buttonsSide()",
    },
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgTemplateOutlet, S25ButtonComponent, S25CollapseChevronComponent, S25IconButtonComponent],
})
export class S25SimpleCollapseComponent extends AngBridge {
    readonly header = input.required<Header>();
    readonly titleText = input("Collapse or Open");
    readonly expanded = model(true);
    readonly hasClose = input(false);
    readonly buttonsSide = input<"left" | "right">("right");

    readonly closed = output<void>();

    readonly headerText = computed((): string | undefined => {
        const header = this.header();
        if (typeof header === "string") return header;
        if (!(header instanceof TemplateRef)) return header.text;
    });

    readonly headerLevel = computed((): 1 | 2 | 3 | 4 | 5 | 6 | undefined => {
        const header = this.header();
        if (typeof header === "object" && !(header instanceof TemplateRef)) return header.level;
    });

    readonly headerTemplate = computed((): TemplateRef<void> | undefined => {
        const header = this.header();
        return header instanceof TemplateRef ? header : undefined;
    });

    public expand() {
        this.expanded.set(true);
    }

    public collapse() {
        this.expanded.set(false);
    }

    public toggle() {
        this.expanded.update((expanded) => !expanded);
    }
}
